import { Color } from "@dotlegal/dotlegal-ui-components";

// Mirrors the backend RiskMitigationPlanStatus enum (numeric, stored as INT).
export enum RiskMitigationPlanStatus {
    Proposed = 1,
    Approved = 2,
    Planned = 3,
    InProgress = 4,
    Completed = 5,
    Deferred = 6,
    Cancelled = 7,
    Promoted = 8,
}

export const mitigationStatusColor: Record<RiskMitigationPlanStatus, Color> = {
    [RiskMitigationPlanStatus.Proposed]: Color.purple,
    [RiskMitigationPlanStatus.Approved]: Color.teal,
    [RiskMitigationPlanStatus.Planned]: Color.lightBlue,
    [RiskMitigationPlanStatus.InProgress]: Color.blue,
    [RiskMitigationPlanStatus.Completed]: Color.green,
    [RiskMitigationPlanStatus.Deferred]: Color.yellow,
    [RiskMitigationPlanStatus.Cancelled]: Color.graniteGray,
    [RiskMitigationPlanStatus.Promoted]: Color.mintGreen,
};

// The library resolves a Color to its background hex internally (GetColorPicker) but does not
// export that helper, so we mirror the background values for the colors used by mitigation statuses.
// Keep in sync with the dotlegal-ui-components palette so card accents match their status chips.
export const colorBackgroundHex: Partial<Record<Color, string>> = {
    [Color.blue]: "#E3F3FD",
    [Color.green]: "#B2EAE4",
    [Color.graniteGray]: "#E7E7EC",
    [Color.lightBlue]: "#E6E5FC",
    [Color.yellow]: "#FAF5BC",
    [Color.teal]: "#D2E3FC",
    [Color.mintGreen]: "#D0F7C3",
    [Color.purple]: "#F4D9F8",
    [Color.gold]: "#FFEBB6",
};

export const mitigationStatusTranslationKey: Record<RiskMitigationPlanStatus, string> = {
    [RiskMitigationPlanStatus.Proposed]: "riskMitigationStatusProposed",
    [RiskMitigationPlanStatus.Approved]: "riskMitigationStatusApproved",
    [RiskMitigationPlanStatus.Planned]: "riskMitigationStatusPlanned",
    [RiskMitigationPlanStatus.InProgress]: "riskMitigationStatusInProgress",
    [RiskMitigationPlanStatus.Completed]: "riskMitigationStatusCompleted",
    [RiskMitigationPlanStatus.Deferred]: "riskMitigationStatusDeferred",
    [RiskMitigationPlanStatus.Cancelled]: "riskMitigationStatusCancelled",
    [RiskMitigationPlanStatus.Promoted]: "riskMitigationStatusPromoted",
};

// Completed/Cancelled/Promoted are terminal — they have no outgoing transitions and render as a
// read-only chip rather than an editable selector.
export function isTerminalMitigationStatus(status: RiskMitigationPlanStatus): boolean {
    return status === RiskMitigationPlanStatus.Completed || status === RiskMitigationPlanStatus.Cancelled || status === RiskMitigationPlanStatus.Promoted;
}

// The valid single-step transitions for the status selector. Mirrors the domain state machine.
export const validStatusTargets: Record<RiskMitigationPlanStatus, Array<RiskMitigationPlanStatus>> = {
    [RiskMitigationPlanStatus.Proposed]: [RiskMitigationPlanStatus.Approved, RiskMitigationPlanStatus.Deferred],
    [RiskMitigationPlanStatus.Approved]: [RiskMitigationPlanStatus.Planned, RiskMitigationPlanStatus.Deferred],
    [RiskMitigationPlanStatus.Planned]: [RiskMitigationPlanStatus.InProgress, RiskMitigationPlanStatus.Completed, RiskMitigationPlanStatus.Deferred],
    [RiskMitigationPlanStatus.InProgress]: [RiskMitigationPlanStatus.Planned, RiskMitigationPlanStatus.Completed, RiskMitigationPlanStatus.Deferred],
    [RiskMitigationPlanStatus.Deferred]: [RiskMitigationPlanStatus.Proposed, RiskMitigationPlanStatus.Approved],
    [RiskMitigationPlanStatus.Completed]: [],
    [RiskMitigationPlanStatus.Cancelled]: [],
    [RiskMitigationPlanStatus.Promoted]: [],
};
